import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Stack, Tooltip, IconButton } from "@mui/material";
import { Star as StarIcon } from "@mui/icons-material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { LeadThread } from "./leadApi";
import { NAVY, GOLD } from "./leadCellUtils";
import LeadNumberBadge from "./LeadNumberBadge";
import UserAvatar from "../../Components/UserAvatar";

interface Props {
  lead:      LeadThread;
  isOverdue: boolean;
}

/**
 * Inquiry-contact cell. Renders the lead's ORIGINAL inquiry identity
 * (the name/email/phone the inquirer submitted). The lead's own name is
 * the click target — it always opens the lead's detail page so the
 * operator never lands on the customer's master profile by accident.
 *
 * If a customer account is linked, a small "linked customer" badge with
 * a separate icon-button is shown next to the name. Clicking THAT badge
 * (not the name) opens /customer-history/{customer_id}.
 */
export const CustomerCell = React.memo(function CustomerCell({ lead, isOverdue }: Props) {
  const navigate = useNavigate();
  const linkedCustomer = lead.customer ?? null;
  const customerName = linkedCustomer?.display_name?.trim() || null;
  const namesDiffer  = !!customerName && customerName.toLowerCase() !== (lead.name ?? "").trim().toLowerCase();

  // Lead name click → ALWAYS open the lead detail page (preserves inquiry
  // identity). The customer profile is reachable via the separate
  // AccountCircle icon below.
  const handleNameClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/leads/${lead.id}`);
  };

  const handleCustomerClick = (e: React.MouseEvent) => {
    if (!lead.customer_id) return;
    e.stopPropagation();
    navigate(`/customer-history/${lead.customer_id}`);
  };

  return (
    <Stack direction="row" spacing={1.6} alignItems="center">
      {/* Avatar — uses the shared UserAvatar so it neutrally renders
          initials on a slate disc (no rainbow colors per the enterprise
          identity rules). Leads don't have profile_image so the
          fallback always wins; if/when a lead becomes a linked
          customer with a stored photo, it would surface here. */}
      <UserAvatar
        user={{
          first_name: lead.name?.split(" ")[0],
          last_name:  lead.name?.split(" ").slice(1).join(" "),
        }}
        size={36}
        sx={{ flexShrink: 0 }}
      />

      {/* Details */}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        {/* Inquiry name + badges */}
        <Stack direction="row" alignItems="center" gap={0.5} mb={0.25}>
          <Typography
            onClick={handleNameClick}
            title="Open inquiry detail"
            sx={{
              fontWeight: 700, color: NAVY, fontSize: "0.83rem",
              textTransform: "capitalize",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              maxWidth: 170,
              cursor: "pointer",
              transition: "color 0.12s",
              "&:hover": { color: "#6366f1", textDecoration: "underline" },
            }}
          >
            {lead.name?.toLowerCase()}
          </Typography>
          {lead.is_vip && (
            <Tooltip title="VIP Client">
              <StarIcon sx={{ fontSize: 13, color: GOLD, flexShrink: 0 }} />
            </Tooltip>
          )}
          {isOverdue && (
            <Tooltip title="Follow-up overdue">
              <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#ef4444", flexShrink: 0 }} />
            </Tooltip>
          )}
          {/* Linked-customer affordance — visible only when the inquiry's
              name differs from the customer account name, so operators
              know two identities exist. Clicks open the customer profile
              SEPARATELY from the inquiry. */}
          {namesDiffer && (
            <Tooltip title={`Linked customer: ${customerName}`}>
              <IconButton
                size="small"
                onClick={handleCustomerClick}
                sx={{ p: 0.25, color: "#6366f1", "&:hover": { color: NAVY } }}
              >
                <AccountCircleIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>

        {/* Public lead number + same-event badges.
            Status badges are muted: low-saturation pastel fills with a
            slate-toned label and a hairline border — they signal state
            without competing with the row content. */}
        <Stack direction="row" alignItems="center" gap={0.5} mb={0.3} flexWrap="wrap">
          {lead.lead_number && (
            <LeadNumberBadge value={lead.lead_number} variant="muted" />
          )}
          {lead.is_primary_event && (
            <Tooltip title="Primary event in its same-event group">
              <Box sx={{
                fontSize: 9, fontWeight: 700, letterSpacing: 0.4,
                bgcolor: "#fafafa", color: "#92400e",
                border: "1px solid #fde68a", borderRadius: 0.6,
                px: 0.55, py: 0.05, textTransform: "uppercase",
              }}>★ Primary</Box>
            </Tooltip>
          )}
          {lead.resolution_status === "same_event_duplicate" && (
            <Tooltip title={lead.related_primary_lead_id ? `Same event as lead #${lead.related_primary_lead_id}` : "Same-event duplicate"}>
              <Box sx={{
                fontSize: 9, fontWeight: 700, letterSpacing: 0.4,
                bgcolor: "#fafafa", color: "#6d28d9",
                border: "1px solid #ddd6fe", borderRadius: 0.6,
                px: 0.55, py: 0.05, textTransform: "uppercase",
              }}>Same Event</Box>
            </Tooltip>
          )}
          {lead.resolution_status === "trash" && (
            <Tooltip title="In trash — restore from admin actions menu">
              <Box sx={{
                fontSize: 9, fontWeight: 700, letterSpacing: 0.4,
                bgcolor: "#fafafa", color: "#475569",
                border: "1px solid #e2e8f0", borderRadius: 0.6,
                px: 0.55, py: 0.05, textTransform: "uppercase",
              }}>Trashed</Box>
            </Tooltip>
          )}
          {lead.resolution_status === "archived" && (
            <Tooltip title={lead.archived_reason || "Archived"}>
              <Box sx={{
                fontSize: 9, fontWeight: 700, letterSpacing: 0.4,
                bgcolor: "#fafafa", color: "#92400e",
                border: "1px solid #fde68a", borderRadius: 0.6,
                px: 0.55, py: 0.05, textTransform: "uppercase",
              }}>Archived</Box>
            </Tooltip>
          )}
        </Stack>

        {/* Organisation */}
        {lead.organization && (
          <Typography sx={{
            fontSize: "0.67rem", color: "#94a3b8", mb: 0.35,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 185,
          }}>
            {lead.organization}
          </Typography>
        )}

        {/* Contact lines — outline icons instead of decorative emoji so
            the row reads as enterprise CRM, not a chat thread. */}
        <Stack spacing={0.2}>
          {lead.phone && (
            <Stack direction="row" alignItems="center" gap={0.6}>
              <PhoneOutlinedIcon sx={{ fontSize: 12, color: "#94a3b8", flexShrink: 0 }} />
              <Typography sx={{ fontSize: "0.71rem", color: "#475569", fontVariantNumeric: "tabular-nums" }}>
                {lead.phone}
              </Typography>
            </Stack>
          )}
          {lead.email && (
            <Stack direction="row" alignItems="center" gap={0.6} sx={{ minWidth: 0 }}>
              <EmailOutlinedIcon sx={{ fontSize: 12, color: "#94a3b8", flexShrink: 0 }} />
              <Typography sx={{
                fontSize: "0.69rem", color: "#64748b",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 175,
              }}>
                {lead.email}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Box>
    </Stack>
  );
});
